import * as React from 'react';
import { PANELDEN_ATANABILIR_ROLLER, ROL_ADLARI, type Rol } from '@hotelcms/shared';
import { ApiHatasi, api } from '@/lib/api';
import { anMs } from '@/lib/utils';

/**
 * Personel hesaplari.
 *
 * Tesis kurulumu yalnizca sahip hesabini acar; resepsiyon, kat hizmetleri ve
 * muhasebe buradan eklenir. Gecici parola sunucuda uretilir ve YALNIZCA
 * olusturma yanitinda bir kez gorunur — hicbir yerde saklanmaz, bu yuzden
 * ekran onu kapatmadan once personele iletmeniz gerektigini soyler.
 */

interface Kullanici {
  id: string;
  ad: string;
  eposta: string;
  telefon: string | null;
  aktif: boolean;
  sonGiris: string | number | null;
  kilitliBitis: string | number | null;
  rol: Rol | null;
  benMiyim: boolean;
}

const ROL_ACIKLAMA: Partial<Record<Rol, string>> = {
  OWNER: 'Her şey: personel, fiyat, raporlar, faturalama.',
  MANAGER: 'Operasyonun tamamı ve raporlar; faturalama hariç.',
  FRONT_DESK: 'Oda planı, check-in, kimlik okuma, KBS, kasa girişi.',
  HOUSEKEEPING: 'Yalnızca oda planı görüntüleme ve kat hizmetleri.',
  ACCOUNTING: 'Folyo, tahsilat, fatura ve mali raporlar.',
};

const ROL_RENK: Partial<Record<Rol, { zemin: string; yazi: string }>> = {
  OWNER: { zemin: '#edf2ff', yazi: '#1f4fd8' },
  MANAGER: { zemin: '#edf2ff', yazi: '#1f4fd8' },
  FRONT_DESK: { zemin: '#e6f7ec', yazi: '#0f7a3d' },
  HOUSEKEEPING: { zemin: '#fff5dc', yazi: '#b9840f' },
  ACCOUNTING: { zemin: '#f4f6fb', yazi: '#5b6480' },
};

function tarih(deger: string | number | null): string {
  const ms = anMs(deger);
  if (ms === null) return 'Hiç girmedi';
  return new Date(ms).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function basHarf(ad: string): string {
  const p = ad.trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? (p[p.length - 1]?.[0] ?? '') : '')).toLocaleUpperCase('tr-TR');
}

export function Kullanicilar() {
  const [liste, setListe] = React.useState<Kullanici[]>([]);
  const [atanabilir, setAtanabilir] = React.useState<Rol[]>([]);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [yeniAcik, setYeniAcik] = React.useState(false);
  const [parola, setParola] = React.useState<{ ad: string; eposta: string; parola: string } | null>(null);
  const [islenen, setIslenen] = React.useState<string | null>(null);

  const yukle = React.useCallback(async () => {
    try {
      const veri = await api<{ kullanicilar: Kullanici[]; atanabilirRoller: Rol[] }>('/api/kullanicilar');
      setListe(veri.kullanicilar);
      setAtanabilir(veri.atanabilirRoller);
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Kullanıcılar yüklenemedi.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  async function degistir(k: Kullanici, govde: Record<string, unknown>) {
    setIslenen(k.id);
    setHata(null);
    try {
      await api(`/api/kullanicilar/${k.id}`, { method: 'PATCH', body: JSON.stringify(govde) });
      await yukle();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Değişiklik kaydedilemedi.');
    } finally {
      setIslenen(null);
    }
  }

  async function parolaSifirla(k: Kullanici) {
    setIslenen(k.id);
    setHata(null);
    try {
      const cevap = await api<{ eposta: string; geciciParola: string }>(
        `/api/kullanicilar/${k.id}/parola-sifirla`,
        { method: 'POST' },
      );
      setParola({ ad: k.ad, eposta: cevap.eposta, parola: cevap.geciciParola });
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Parola sıfırlanamadı.');
    } finally {
      setIslenen(null);
    }
  }

  const aktifSayisi = liste.filter((k) => k.aktif).length;

  return (
    <main className="flex flex-1 flex-col gap-[22px] bg-zemin p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-3.5 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both]">
        <h1 className="text-3xl font-bold tracking-[-.03em]">Kullanıcılar</h1>
        <span className="text-sm text-gri">
          {aktifSayisi} aktif hesap{liste.length !== aktifSayisi ? ` · ${liste.length - aktifSayisi} pasif` : ''}
        </span>
        <button
          onClick={() => setYeniAcik(true)}
          className="ml-auto flex items-center gap-2 rounded-xl bg-marka-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_-14px_rgba(31,79,216,.8)] transition hover:bg-marka-700"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Personel ekle
        </button>
      </div>

      {hata && (
        <p role="alert" className="rounded-2xl border border-kirmizi/30 bg-[#fdeceb] px-4 py-3 text-sm font-medium text-kirmizi">
          {hata}
        </p>
      )}

      {yukleniyor ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[88px] animate-pulse rounded-[18px] bg-white" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {liste.map((k, i) => {
            const renk = (k.rol && ROL_RENK[k.rol]) ?? { zemin: '#f4f6fb', yazi: '#5b6480' };
            const kilitli = (anMs(k.kilitliBitis) ?? 0) > Date.now();
            const yonetebilir = !k.rol || atanabilir.includes(k.rol);
            return (
              <div
                key={k.id}
                className={
                  'flex flex-wrap items-center gap-x-5 gap-y-3 rounded-[18px] border border-cizgi bg-white p-[18px] transition [animation:popin_.4s_cubic-bezier(.2,.8,.2,1)_both] ' +
                  (k.aktif ? '' : 'opacity-60')
                }
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <span
                  className="grid size-11 flex-none place-items-center rounded-full text-sm font-bold"
                  style={{ background: renk.zemin, color: renk.yazi }}
                >
                  {basHarf(k.ad)}
                </span>

                <div className="flex min-w-[180px] flex-1 flex-col gap-0.5">
                  <span className="flex flex-wrap items-center gap-2 text-[15px] font-semibold text-lacivert">
                    {k.ad}
                    {k.benMiyim && (
                      <span className="rounded-full bg-zemin px-2 py-0.5 text-[11px] font-bold text-gri">siz</span>
                    )}
                    {!k.aktif && (
                      <span className="rounded-full bg-satir px-2 py-0.5 text-[11px] font-bold text-gri">pasif</span>
                    )}
                    {kilitli && (
                      <span className="rounded-full bg-[#fdeceb] px-2 py-0.5 text-[11px] font-bold text-kirmizi">
                        geçici kilitli
                      </span>
                    )}
                  </span>
                  <span className="text-[13px] text-gri">{k.eposta}</span>
                </div>

                <div className="flex min-w-[150px] flex-col gap-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[.08em] text-gri">Rol</span>
                  {k.benMiyim || !yonetebilir || !k.rol ? (
                    <span className="text-sm font-semibold text-lacivert">
                      {k.rol ? ROL_ADLARI[k.rol] : 'Rol atanmamış'}
                    </span>
                  ) : (
                    <select
                      value={k.rol}
                      disabled={islenen === k.id}
                      onChange={(e) => degistir(k, { rol: e.target.value })}
                      className="rounded-lg border-[1.5px] border-cizgi bg-white px-2 py-1 text-sm font-semibold text-lacivert outline-none focus:border-marka-600"
                    >
                      {atanabilir.map((r) => (
                        <option key={r} value={r}>{ROL_ADLARI[r]}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="flex min-w-[140px] flex-col gap-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[.08em] text-gri">Son giriş</span>
                  <span className="text-[13px] text-lacivert">{tarih(k.sonGiris)}</span>
                </div>

                <div className="ml-auto flex flex-wrap gap-2">
                  <button
                    onClick={() => parolaSifirla(k)}
                    disabled={islenen === k.id || !yonetebilir}
                    className="rounded-full border-[1.5px] border-cizgi bg-white px-3.5 py-2 text-[13px] font-semibold text-lacivert transition hover:border-lacivert disabled:opacity-40"
                  >
                    Parola sıfırla
                  </button>
                  {!k.benMiyim && yonetebilir && (
                    <button
                      onClick={() => degistir(k, { aktif: !k.aktif })}
                      disabled={islenen === k.id}
                      className={
                        'rounded-full px-3.5 py-2 text-[13px] font-semibold transition disabled:opacity-40 ' +
                        (k.aktif
                          ? 'border-[1.5px] border-cizgi bg-white text-kirmizi hover:border-kirmizi'
                          : 'bg-yesil text-white hover:brightness-95')
                      }
                    >
                      {k.aktif ? 'Erişimi kapat' : 'Erişimi aç'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {yeniAcik && (
        <YeniPersonel
          atanabilir={atanabilir}
          onKapat={() => setYeniAcik(false)}
          onEklendi={async (sonuc) => {
            setYeniAcik(false);
            setParola(sonuc);
            await yukle();
          }}
        />
      )}

      {parola && <ParolaPenceresi bilgi={parola} onKapat={() => setParola(null)} />}
    </main>
  );
}

/* ------------------------------------------------------------ yeni hesap */

const GIRDI =
  'w-full rounded-xl border-[1.5px] border-cizgi bg-white px-3.5 py-2.5 text-[15px] text-lacivert outline-none transition focus:border-marka-600 focus:shadow-[0_0_0_4px_rgba(31,79,216,.12)]';

function YeniPersonel({
  atanabilir,
  onKapat,
  onEklendi,
}: {
  atanabilir: Rol[];
  onKapat: () => void;
  onEklendi: (s: { ad: string; eposta: string; parola: string }) => void;
}) {
  const [ad, setAd] = React.useState('');
  const [eposta, setEposta] = React.useState('');
  const [telefon, setTelefon] = React.useState('');
  const [rol, setRol] = React.useState<Rol>(
    atanabilir.includes('FRONT_DESK') ? 'FRONT_DESK' : (atanabilir[0] ?? 'FRONT_DESK'),
  );
  const [hata, setHata] = React.useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && !gonderiliyor && onKapat();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onKapat, gonderiliyor]);

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    if (!ad.trim() || !eposta.trim()) return;
    setGonderiliyor(true);
    setHata(null);
    try {
      const cevap = await api<{ eposta: string; geciciParola: string }>('/api/kullanicilar', {
        method: 'POST',
        body: JSON.stringify({ ad: ad.trim(), eposta: eposta.trim(), telefon: telefon.trim() || null, rol }),
      });
      onEklendi({ ad: ad.trim(), eposta: cevap.eposta, parola: cevap.geciciParola });
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Hesap açılamadı.');
      setGonderiliyor(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-lacivert/60 [animation:fadein_.2s_both]" onClick={onKapat} role="presentation">
      <div className="flex min-h-full items-center justify-center p-4">
        <form
          onSubmit={gonder}
          onClick={(e) => e.stopPropagation()}
          className="flex w-full max-w-[480px] flex-col gap-4 rounded-[20px] bg-white p-6 shadow-[0_40px_80px_-32px_rgba(11,21,48,.55)] [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
        >
          <div>
            <h2 className="text-[20px] font-bold tracking-[-.01em] text-lacivert">Personel ekle</h2>
            <p className="mt-0.5 text-[13px] text-gri">
              Hesap açılınca tek kullanımlık bir parola üretilir; onu personele siz iletirsiniz.
            </p>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[12.5px] font-semibold text-gri">Ad soyad</span>
            <input value={ad} onChange={(e) => setAd(e.target.value)} autoFocus required className={GIRDI} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12.5px] font-semibold text-gri">E-posta</span>
            <input type="email" value={eposta} onChange={(e) => setEposta(e.target.value)} required className={GIRDI} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="flex items-baseline justify-between text-[12.5px] font-semibold text-gri">
              Telefon <span className="font-medium">İsteğe bağlı</span>
            </span>
            <input value={telefon} onChange={(e) => setTelefon(e.target.value)} placeholder="+90 5xx xxx xx xx" className={GIRDI} />
          </label>

          <div className="flex flex-col gap-1.5">
            <span className="text-[12.5px] font-semibold text-gri">Rol</span>
            <div className="flex flex-col gap-2">
              {PANELDEN_ATANABILIR_ROLLER.filter((r) => atanabilir.includes(r)).map((r) => (
                <label
                  key={r}
                  className={
                    'flex cursor-pointer gap-3 rounded-[14px] border-[1.5px] p-3 transition ' +
                    (rol === r ? 'border-marka-600 bg-mavi-zemin' : 'border-cizgi bg-white hover:border-mavi-acik')
                  }
                >
                  <input type="radio" name="rol" checked={rol === r} onChange={() => setRol(r)} className="sr-only" />
                  <span
                    className={
                      'mt-0.5 grid size-4 flex-none place-items-center rounded-full border-[1.5px] ' +
                      (rol === r ? 'border-marka-600' : 'border-cizgi')
                    }
                  >
                    {rol === r && <span className="size-2 rounded-full bg-marka-600" />}
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-lacivert">{ROL_ADLARI[r]}</span>
                    <span className="text-[12.5px] leading-relaxed text-gri">{ROL_ACIKLAMA[r]}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {hata && <p role="alert" className="rounded-xl bg-[#fdeceb] px-3.5 py-3 text-[13px] font-medium text-kirmizi">{hata}</p>}

          <div className="mt-1 flex gap-3">
            <button
              type="button"
              onClick={onKapat}
              disabled={gonderiliyor}
              className="flex-1 rounded-full border-[1.5px] border-cizgi bg-white py-3 text-[15px] font-semibold text-lacivert transition hover:border-gri"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={gonderiliyor}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-marka-600 py-3 text-[15px] font-semibold text-white transition hover:bg-marka-700 disabled:opacity-50"
            >
              {gonderiliyor && <span className="size-4 rounded-full border-2 border-white/35 border-t-white [animation:spin_.7s_linear_infinite]" />}
              {gonderiliyor ? 'Açılıyor…' : 'Hesabı aç'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------- tek seferlik parola */

function ParolaPenceresi({
  bilgi,
  onKapat,
}: {
  bilgi: { ad: string; eposta: string; parola: string };
  onKapat: () => void;
}) {
  const [kopyalandi, setKopyalandi] = React.useState(false);
  const metin = `otelcms giriş bilgileri\nAdres: ${location.origin}\nE-posta: ${bilgi.eposta}\nGeçici parola: ${bilgi.parola}`;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-lacivert/60 p-4 [animation:fadein_.2s_both]" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-[460px] rounded-[20px] bg-white p-6 shadow-[0_40px_80px_-32px_rgba(11,21,48,.55)] [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
      >
        <h2 className="text-[20px] font-bold tracking-[-.01em] text-lacivert">{bilgi.ad} için geçici parola</h2>
        <p className="mt-1 text-[13.5px] leading-relaxed text-gri">
          Bu parola <strong className="text-lacivert">yalnızca şimdi</strong> görünür; hiçbir yerde saklanmıyor.
          Kapatmadan önce personele iletin. Unutulursa buradan yenisini üretebilirsiniz.
        </p>

        <div className="mt-4 flex flex-col gap-1 rounded-[14px] bg-lacivert p-4 text-white">
          <span className="text-[11px] font-bold uppercase tracking-[.08em] text-mavi-acik">E-posta</span>
          <span className="text-[15px] font-semibold">{bilgi.eposta}</span>
          <span className="mt-2 text-[11px] font-bold uppercase tracking-[.08em] text-mavi-acik">Geçici parola</span>
          <span className="font-mono text-[22px] font-bold tracking-[.08em]">{bilgi.parola}</span>
        </div>

        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(metin);
                setKopyalandi(true);
                setTimeout(() => setKopyalandi(false), 1600);
              } catch {
                setKopyalandi(false);
              }
            }}
            className="flex-1 rounded-full border-[1.5px] border-cizgi bg-white py-3 text-[15px] font-semibold text-lacivert transition hover:border-lacivert"
          >
            {kopyalandi ? 'Kopyalandı' : 'Giriş bilgilerini kopyala'}
          </button>
          <button
            type="button"
            onClick={onKapat}
            className="flex-1 rounded-full bg-marka-600 py-3 text-[15px] font-semibold text-white transition hover:bg-marka-700"
          >
            İlettim, kapat
          </button>
        </div>
      </div>
    </div>
  );
}
